import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, DoorOpen, RefreshCw, Upload, Download, X, MessageCircle, Fingerprint } from "lucide-react";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", photo_url: "",
  document_id: "", card_number: "", zk_user_id: "", plan_id: "", notes: "",
};

const badge = (s) => ({
  active: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/10",
  expired: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10",
  pending: "text-[#FFD60A] border-[#FFD60A]/30 bg-[#FFD60A]/10",
}[s]);
const label = (s) => ({ active: "Activa", expired: "Vencida", pending: "Pendiente" }[s]);

export default function Members() {
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null); // null closed, obj open
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/members?search=${encodeURIComponent(search)}&status=${status}`).then((r) => setMembers(r.data));
  }, [search, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const openNew = () => setForm({ ...empty });
  const openEdit = (m) => setForm({ ...empty, ...m });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.status; delete payload.expiry_date; delete payload.plan_name;
      if (form.id) await api.put(`/members/${form.id}`, payload);
      else await api.post("/members", payload);
      toast.success("Socio guardado");
      setForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (m) => {
    if (!window.confirm(`¿Eliminar a ${m.first_name} ${m.last_name}?`)) return;
    await api.delete(`/members/${m.id}`); toast.success("Socio eliminado"); load();
  };

  const checkin = async (m) => {
    const { data } = await api.post("/checkin", { member_id: m.id, method: "manual" });
    if (data.access === "granted") toast.success(`Acceso permitido · ${m.first_name}`);
    else toast.error(`Acceso negado · ${data.reason}`);
  };

  const renew = async (m) => {
    if (!m.plan_id) { toast.error("Asigna un plan primero"); return; }
    await api.post(`/members/${m.id}/renew`); toast.success("Membresía renovada"); load();
  };

  const enrollFingerprint = async () => {
    if (!form?.id) return;
    if (!form.zk_user_id?.trim()) { toast.error("Asigna primero el ZK User ID (Pin)"); return; }
    try {
      const { data } = await api.post(`/members/${form.id}/fingerprint/enroll`);
      setForm((f) => ({ ...f, fingerprint_status: data.fingerprint_status }));
      toast.success("Solicitud enviada. Coloca el dedo 3 veces en el lector USB de recepción.");
      pollFingerprint(form.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pollFingerprint = (id) => {
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/members/${id}`);
        setForm((f) => (f && f.id === id ? { ...f, fingerprint_status: data.fingerprint_status, fingerprint_error: data.fingerprint_error } : f));
        if (data.fingerprint_status === "enrolled") { clearInterval(t); toast.success("Huella registrada correctamente"); }
        else if (data.fingerprint_status === "failed") { clearInterval(t); toast.error(data.fingerprint_error || "No se pudo capturar la huella"); }
      } catch (e) { /* keep polling */ }
      if (tries >= 40) clearInterval(t);
    }, 3000);
  };

  const deleteFingerprint = async () => {
    if (!form?.id) return;
    await api.delete(`/members/${form.id}/fingerprint`);
    setForm((f) => ({ ...f, fingerprint_status: "", fingerprint_error: "" }));
    toast.success("Huella eliminada");
  };

  const sendReminder = async (m) => {
    if (!m.phone) { toast.error("El socio no tiene teléfono"); return; }
    const { data } = await api.post(`/notifications/send/${m.id}?ntype=reminder_3d`);
    if (data.status === "sent") toast.success(`WhatsApp enviado a ${m.first_name}`);
    else if (data.status === "not_configured") toast.error("WhatsApp no configurado (Twilio)");
    else toast.error(data.error || "No se pudo enviar");
  };

  const exportCsv = () => window.open(`${API}/zk/export-members`, "_blank");

  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/zk/import-members", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importados ${data.imported}, actualizados ${data.updated}`); load();
    } catch (err) { toast.error("Error al importar CSV"); }
    e.target.value = "";
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="overline text-[#00f0ff]">Base de datos</p>
          <h1 className="font-display text-5xl">SOCIOS</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <label data-testid="import-csv-button" className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5 cursor-pointer">
            <Upload className="w-4 h-4" /> Importar ZK
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" />
          </label>
          <button data-testid="export-csv-button" onClick={exportCsv} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5">
            <Download className="w-4 h-4" /> Exportar ZK
          </button>
          <button data-testid="member-add-button" onClick={openNew} className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#00c2cc]">
            <Plus className="w-4 h-4" /> Nuevo socio
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="member-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, documento o tarjeta..."
            className="w-full bg-[#121212] border border-white/10 pl-10 pr-4 py-2.5 text-sm focus:border-[#00f0ff] focus:outline-none"
          />
        </div>
        {["", "active", "expired", "pending"].map((s) => (
          <button key={s} data-testid={`filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-4 py-2.5 text-sm border ${status === s ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50 hover:text-white"}`}>
            {s === "" ? "Todos" : label(s)}
          </button>
        ))}
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="members-table">
          <thead>
            <tr className="text-left overline border-b border-white/10">
              <th className="px-4 py-4">Socio</th>
              <th className="px-4 py-4">Documento / Tarjeta</th>
              <th className="px-4 py-4">Plan</th>
              <th className="px-4 py-4">Vence</th>
              <th className="px-4 py-4">Estado</th>
              <th className="px-4 py-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-white/30">Sin socios registrados.</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1a1a1a] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                      {m.photo_url ? <img src={m.photo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-white/40">{m.first_name?.[0]}{m.last_name?.[0]}</span>}
                    </div>
                    <div><p className="font-semibold flex items-center gap-1.5">{m.first_name} {m.last_name}{m.fingerprint_status === "enrolled" && <Fingerprint data-testid={`fingerprint-badge-${m.id}`} className="w-3.5 h-3.5 text-[#39FF14]" title="Huella registrada" />}</p><p className="text-xs text-white/40">{m.phone || m.email}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-logs text-xs text-white/60">{m.document_id || "—"} {m.card_number ? `· ${m.card_number}` : ""}</td>
                <td className="px-4 py-3">{m.plan_name || <span className="text-white/30">—</span>}</td>
                <td className="px-4 py-3 font-mono-logs text-xs">{m.expiry_date ? new Date(m.expiry_date).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-bold uppercase border ${badge(m.status)}`}>{label(m.status)}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Check-in" data-testid={`checkin-${m.id}`} onClick={() => checkin(m)} className="p-2 hover:bg-white/10 text-[#00f0ff]"><DoorOpen className="w-4 h-4" /></button>
                    <button title="WhatsApp recordatorio" data-testid={`whatsapp-${m.id}`} onClick={() => sendReminder(m)} className="p-2 hover:bg-white/10 text-[#25D366]"><MessageCircle className="w-4 h-4" /></button>
                    <button title="Renovar" data-testid={`renew-${m.id}`} onClick={() => renew(m)} className="p-2 hover:bg-white/10 text-[#39FF14]"><RefreshCw className="w-4 h-4" /></button>
                    <button title="Editar" data-testid={`edit-${m.id}`} onClick={() => openEdit(m)} className="p-2 hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                    <button title="Eliminar" data-testid={`delete-${m.id}`} onClick={() => del(m)} className="p-2 hover:bg-white/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
          <form onSubmit={save} data-testid="member-form" className="bg-[#0a0a0a] border border-white/20 w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display text-2xl">{form.id ? "EDITAR SOCIO" : "NUEVO SOCIO"}</h3>
              <button type="button" onClick={() => setForm(null)}><X className="w-5 h-5 text-white/50 hover:text-white" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[
                ["first_name", "Nombre *", "text", true], ["last_name", "Apellido *", "text", true],
                ["phone", "Teléfono", "text"], ["email", "Email", "email"],
                ["document_id", "Documento / DNI", "text"], ["card_number", "N° Tarjeta RFID", "text"],
                ["zk_user_id", "ZK User ID (Pin)", "text"], ["photo_url", "URL Foto", "text"],
              ].map(([k, lbl, type, req]) => (
                <div key={k} className={k === "photo_url" ? "col-span-2" : ""}>
                  <label className="overline block mb-1.5">{lbl}</label>
                  <input
                    data-testid={`field-${k}`} type={type} required={req} value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none"
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="overline block mb-1.5">Plan de membresía</label>
                <select
                  data-testid="field-plan" value={form.plan_id || ""}
                  onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                  className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none"
                >
                  <option value="">Sin plan</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · ${p.price} · {p.duration_days}d</option>)}
                </select>
              </div>
              {form.id && (
                <div className="col-span-2 border border-white/10 bg-[#0d0d0d] p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Fingerprint className="w-6 h-6 text-[#00f0ff]" />
                      <div>
                        <p className="text-sm font-semibold">Huella dactilar (lector USB)</p>
                        <p className="text-xs text-white/40">
                          {form.fingerprint_status === "enrolled" && <span className="text-[#39FF14]">✓ Registrada</span>}
                          {form.fingerprint_status === "requested" && <span className="text-[#FFD60A]">Esperando captura en recepción…</span>}
                          {form.fingerprint_status === "failed" && <span className="text-[#FF3B30]">{form.fingerprint_error || "Falló la captura"}</span>}
                          {!form.fingerprint_status && "Sin huella registrada"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {form.fingerprint_status === "enrolled" && (
                        <button type="button" data-testid="fingerprint-delete-button" onClick={deleteFingerprint}
                          className="border border-white/20 px-3 py-2 text-xs hover:bg-white/5">Eliminar</button>
                      )}
                      <button type="button" data-testid="fingerprint-enroll-button" onClick={enrollFingerprint}
                        disabled={form.fingerprint_status === "requested"}
                        className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-xs px-4 py-2 hover:bg-[#00c2cc] disabled:opacity-50">
                        <Fingerprint className="w-4 h-4" />
                        {form.fingerprint_status === "requested" ? "En proceso…" : "Capturar huella"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setForm(null)} className="border border-white/20 px-5 py-2.5 text-sm hover:bg-white/5">Cancelar</button>
              <button type="submit" data-testid="member-save-button" disabled={saving} className="bg-[#00f0ff] text-black font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#00c2cc] disabled:opacity-50">
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
